import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import EmployeeDocumentModal from "./EmployeeDocumentModal"; // assuming this exists
import { useAuth } from './../../../context/AuthContext';
import withPermission from './../../../utils/withPermission';

const EmployeeDocument = ({ setEmployeeDetails, EmployeeData, fetchEmployeeData,EmployeeId }) => {
  const [documents, setEmployeeDocument] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedEmployeeDocumentId, setSelectedEmployeeDocumentId] = useState(null);
  const [selectedEmployeeDocumentData, setSelectedEmployeeDocumentData] = useState(null);
  const [errors, setErrors] = useState({});
  const didFetchRef = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const { id } = useParams(); // employee id in route
  const { roles, permissions } = useAuth();
  const WithDeletePermission = withPermission(({ children }) => children, ['employeedoc-delete']);
  const WithEditPermission = withPermission(({ children }) => children, ['employeedoc-edit']);
  const WithCreatePermission = withPermission(({ children }) => children, ['employeedoc-create']);
  const [searchQuery, setSearchQuery] = useState('');
  const debounceTimeoutRef = useRef(null);

  // Helper: extract document array from various possible API shapes
  const extractArray = (response) => {
    const payload = response?.data;
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.data?.data)) return payload.data.data;
    if (Array.isArray(payload.documents)) return payload.documents;
    return [];
  };

  // Helper: extract pagination meta if present
  const extractMeta = (response) => {
    const payload = response?.data;
    if (!payload) return {};
    if (payload.meta) return payload.meta;
    if (payload.data?.meta) return payload.data.meta;
    if (payload.data?.data?.meta) return payload.data.data.meta;
    return {};
  };

  // Debounced search effect
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      // page 1 on new search
      fetchEmployeeDocument(1, id);
    }, searchQuery ? 600 : 0); // shorter debounce

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // FETCH documents for an employee
  const fetchEmployeeDocument = async (page = 1, employeeId = id) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');
      const token = userInfo?.token;

      if (!employeeId) {
        console.warn('No employee id provided for fetching documents.');
        setEmployeeDocument([]);
        setPageCount(1);
        setCurrentPage(1);
        return;
      }

      const url = `${backendUrl}/api/employees/${employeeId}/documents`;
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (page) params.page = page;

      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Tenant-Origin': origin,
        },
        params,
      });

      const docs = extractArray(response);
      setEmployeeDocument(docs);

      const meta = extractMeta(response);
      const pages = meta.total_pages || meta.last_page || 1;
      setPageCount(pages);
      setCurrentPage(meta.current_page || page);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setEmployeeDocument([]);
      setPageCount(1);
      setCurrentPage(1);
    }
  };

  // Create/upload document (supports file)
  // Create/upload document (supports file)
const addEmployeeDocument = async (formData) => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const userInfo = JSON.parse(localStorage.getItem('user-info'));
    const origin = localStorage.getItem('user-origin');
    const token = userInfo?.token;

    const employeeId = id;
    if (!employeeId) throw new Error('Employee ID is missing');

    const response = await axios.post(
      `${backendUrl}/api/employees/${employeeId}/documents`,
      formData, // ✅ use directly
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Tenant-Origin': origin,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    const created = response.data?.data || response.data || null;
    if (created) {
      setEmployeeDocument((prev) => [...prev, created]);
    }

    handleCloseModal();
  } catch (error) {
    console.error('Error adding EmployeeDocument:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return { message: 'Unexpected error' };
  }
};

  const editEmployeeDocument = async (EmployeeDocumentId, formData) => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const userInfo = JSON.parse(localStorage.getItem('user-info'));
    const origin = localStorage.getItem('user-origin');
    const token = userInfo?.token;

    const response = await axios.post(
      `${backendUrl}/api/employees/documents/${EmployeeDocumentId}?_method=PUT`, 
      formData,   // ✅ send directly, don’t wrap again
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Tenant-Origin': origin,
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    const updated = response.data?.data || response.data || null;
    if (updated) {
      setEmployeeDocument((prev) =>
        prev.map((doc) => (doc.id === EmployeeDocumentId ? updated : doc))
      );
    }

    handleCloseModal();
  } catch (error) {
    console.error('Error editing EmployeeDocument:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return { message: 'Unexpected error' };
  }
};


  // DELETE document
  const deleteEmployeeDocument = async (docId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
          const userInfo = JSON.parse(localStorage.getItem('user-info'));
          const origin = localStorage.getItem('user-origin');
          const token = userInfo?.token;

          await axios.delete(`${backendUrl}/api/employees/documents/${docId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Tenant-Origin': origin,
            },
          });

          setEmployeeDocument((prev) => prev.filter((d) => d.id !== docId));
          Swal.fire("Deleted!", "The document has been deleted.", "success");
        } catch (error) {
          console.error('Error deleting EmployeeDocument:', error);
          Swal.fire("Error!", "Failed to delete document. Please try again.", "error");
        }
      }
    });
  };

  const handleShowModal = (EmployeeDocumentId = null) => {
    if (EmployeeDocumentId) {
      const EmployeeDocumentToEdit = documents.find((d) => d.id === EmployeeDocumentId);
      setSelectedEmployeeDocumentData(EmployeeDocumentToEdit || null);
    } else {
      setSelectedEmployeeDocumentData(null);
      setErrors({});
    }
    setSelectedEmployeeDocumentId(EmployeeDocumentId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEmployeeDocumentId(null);
    setSelectedEmployeeDocumentData(null);
    setErrors({});
    // refresh list after close to ensure updated statuses etc.
    fetchEmployeeDocument(currentPage, id);
  };

  const handlePageChange = ({ selected }) => {
    const newPage = selected + 1;
    setCurrentPage(newPage);
    fetchEmployeeDocument(newPage, id);
  };

  // initial fetch on mount / when route id changes
  useEffect(() => {
    if (!didFetchRef.current) {
      fetchEmployeeDocument(1, id);
      didFetchRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Render content (kept same markup as you supplied, but columns now correspond to real fields)
  return (
    <React.Fragment>
      <div>
        <div>
          <h4 className="mb-sm-0 font-size-18">Employee Documents</h4>
          <div className="d-flex justify-content-end mb-2">
            <div className="search-box me-2">
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control font-size-13"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <i className="bx bx-search-alt search-icon" />
              </div>
            </div>

            <WithCreatePermission>
              <button
                type="button"
                className="btn rounded-circle border d-flex align-items-center justify-content-center round-plus"
                onClick={() => handleShowModal()}
                title="Add Document"
              >
                <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
              </button>
            </WithCreatePermission>
          </div><br />

          <table className="table table-hover datatable dt-responsive nowrap view-table font-size-13" >
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Document Type</th>
                <th scope="col">Document Number</th>
                <th scope="col">Issue Date</th>
                <th scope="col">Expiry Date</th>
                <th scope="col">Status</th>
                <th scope="col">File</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.length > 0 ? (
                documents.map((doc, index) => (
                  <tr key={doc.id}>
                    <td>{index + 1}</td>
                    <td>{doc.document_type}</td>
                    <td>{doc.document_number || '-'}</td>
                    <td>{doc.issue_date || '-'}</td>
                    <td>{doc.expiry_date || '-'}</td>
                    <td>
                      <span
                        className={`badge ${
                          doc.status === "valid"
                            ? "bg-success"
                            : doc.status === "expired"
                            ? "bg-danger"
                            : "bg-warning"
                        }`}
                      >
                        {doc.status}
                      </span>
                    </td>
                    <td>
                      {doc.file_path ? (
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/storage/${doc.file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View File
                        </a>
                      ) : "No File"}
                    </td>
                    <td>
                      <div className="dropdown">
                        <i
                          className="mdi mdi-dots-horizontal font-size-18"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          style={{ cursor: 'pointer' }}
                        ></i>
                        <ul className="dropdown-menu dropdown-menu-end font-size-14">
                          <li>
                            <WithEditPermission>
                              <button
                                className="dropdown-item"
                                onClick={() => handleShowModal(doc.id)}
                              >
                                Edit
                              </button>
                            </WithEditPermission>
                          </li>
                          <li>
                            <WithDeletePermission>
                              <button
                                className="dropdown-item"
                                onClick={() => deleteEmployeeDocument(doc.id)}
                              >
                                Delete
                              </button>
                            </WithDeletePermission>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8">No documents available</td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="d-flex justify-content-end pagination-sm">
            <ReactPaginate
              previousLabel={'<<'}
              nextLabel={'>>'}
              breakLabel={'...'}
              pageCount={pageCount}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageChange}
              containerClassName={'pagination justify-content-center pagination-sm'}
              pageClassName={'page-item'}
              pageLinkClassName={'page-link'}
              previousClassName={'page-item'}
              previousLinkClassName={'page-link'}
              nextClassName={'page-item'}
              nextLinkClassName={'page-link'}
              breakClassName={'page-item'}
              breakLinkClassName={'page-link'}
              activeClassName={'active'}
            />
          </div>

        </div>
      </div>

      <EmployeeDocumentModal
        showModal={showModal}
        handleClose={handleCloseModal}
        EmployeeDocumentId={selectedEmployeeDocumentId}
        EmployeeDocumentData={selectedEmployeeDocumentData}
        addEmployeeDocument={addEmployeeDocument}
        editEmployeeDocument={editEmployeeDocument}
        errors={errors}
        setErrors={setErrors}
      />
    </React.Fragment>
  );
};

export default EmployeeDocument;
